import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CONSTANT } from "src/app/constant";
import { OBAttempt } from "src/app/models/models";

/** Parse codes like "kno_1" or "KNO1" → { cat: "KNO", num: 1 } */
export function parseObsCode(raw?: string): { cat: string; num: number | null } {
	const s = (raw ?? "").trim();
	const m = s.match(/^([a-zA-Z]+(?:[_\s-][a-zA-Z]+)*)[_\s-]*([0-9]+)?$/);
	return { cat: (m?.[1] ?? "").toUpperCase(), num: m?.[2] ? parseInt(m[2], 10) : null };
}

/** Parse labels like "KNO 1" → { cat: "KNO", num: 1 } */
export function parseObLabel(raw?: string): { cat: string; num: number | null } {
	const s = (raw ?? "").trim();
	const m = s.match(/^([a-zA-Z]+(?:[_\s-][a-zA-Z]+)*)[_\s-]*([0-9]+)?$/);
	return { cat: (m?.[1] ?? "").toUpperCase(), num: m?.[2] ? parseInt(m[2], 10) : null };
}

/** Default observation state for a single OB cell. */
export function makeObs(fixedManeuverCode: string): OBAttempt {
	return {
		totalCount: 0,
		currentCount: 0,
		preDefined: false,
		lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
		maneuverCode: fixedManeuverCode,
	};
}

/** Pull category code from an observation record. */
export function categoryFrom(observation: { name?: string; observation_category?: { code?: string } }): string {
	return (observation?.observation_category?.code ?? "").toUpperCase();
}

/**
 * Build a structure keyed by category (KNO/PRO/COM) → keyed by "KNO 1" → OBAttempt.
 * This is used to prepare the initial template for a maneuver.
 */
export function buildCategoriesFromData(
	observations: Array<{ name?: string; observation_category?: { code?: string }; fixedManeuver?: any }>,
	fixedManuverCode: string = "",
): Record<string, Record<string, OBAttempt>> {
	const cats: Record<string, Record<string, OBAttempt>> = {};

	for (const o of observations ?? []) {
		const cat = categoryFrom(o);
		if (!cat) continue;
		const key = (o.name ?? "").trim();
		if (!key) continue;
		cats[cat] ??= {};
		if (!cats[cat][key]) cats[cat][key] = makeObs(o?.fixedManeuver?.fixed_maneuver);
	}
	return cats;
}

/** Prefer "KNO 1", but some places use "KNO1". */
export function obKey(cat: string, num: number, preferSpace = true): string {
	return preferSpace ? `${cat} ${num}` : `${cat}${num}`;
}

/**
 * From a category template object, build a FormGroup of sections with OB controls.
 * The template is the data shape returned by buildCategoriesFromData, without "maneuver".
 */
export function buildSectionsFromTemplate(
	fb: UntypedFormBuilder,
	template: Record<string, unknown>,
	maneuverCode: string = "",
): Record<string, UntypedFormGroup> {
	const sections: Record<string, UntypedFormGroup> = {};
	for (const [sectionName, items] of Object.entries(template)) {
		if (!items || typeof items !== "object") continue;

		const itemKeys = Object.keys(items as Record<string, any>);
		const ctrls = itemKeys.reduce((acc: Record<string, UntypedFormControl>, key) => {
			acc[key] = fb.control({
				value: {
					totalCount: 0,
					currentCount: 0,
					preDefined: false,
					lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
					fixedManeuverCode: maneuverCode,
				},
				disabled: false,
			});
			return acc;
		}, {});
		sections[sectionName] = fb.group(ctrls);
	}
	return sections;
}

/** Utility: normalize possibly-string booleans from storage to real booleans. */
export function coerceBool(v: any, fallback: boolean | undefined = undefined): boolean | undefined {
	if (typeof v === "boolean") return v;
	if (typeof v === "string") {
		const s = v.trim().toLowerCase();
		if (s === "true" || s === "1" || s === "yes") return true;
		if (s === "false" || s === "0" || s === "no") return false;
	}
	if (typeof v === "number") return v === 1;
	return fallback;
}
